import { useInfiniteQuery, useQueryClient } from "react-query";
import { useMemo } from "react";
import { supabase } from "../../../config/supabaseClient";
import { StaticVariables } from "../../StaticVariables";
import { normalizeCustomContentPostIds } from "./normalizeCustomContentPostIds";
import {
  fetchShopLinkMap,
  filterPostsWithShopLinks,
} from "../../helpers/shoppableContent";

/** Matches LiB query feeds / website template batch size. */
export const CUSTOM_CONTENT_PAGE_SIZE = 25;

type CustomContentPage = {
  data: any[];
  /** Offset into the selected id list for the next page. */
  nextOffset: number;
  totalIds: number;
  pageSize: number;
};

/**
 * Website-template custom content: paginate by slicing post_ids so we are not
 * capped by `get_custom_content_orderedd` returning ~100 rows for a full id list.
 */
export function useLoadCustomContentInfinite(
  username: any,
  user_id: any,
  postIds: any,
  title: any,
  order?: any,
  custom_order?: any,
  activeFilter?: any,
  shoppableStatusFilter: string = "all",
  enabled: boolean = true,
) {
  const queryClient = useQueryClient();
  const contentOrder = order;

  const normalizedPostIds = useMemo(
    () => normalizeCustomContentPostIds(postIds),
    [postIds],
  );

  const normalizedCustomOrder = useMemo(() => {
    const ids = normalizeCustomContentPostIds(custom_order);
    return ids.length ? ids : null;
  }, [custom_order]);

  const platforms = useMemo(() => {
    const staticVars = StaticVariables();
    if (activeFilter === "content") {
      return staticVars.CONTENT_PLATFORMS;
    }
    if (activeFilter === "products") {
      return staticVars.PRODUCT_PLATFORMS;
    }
    return staticVars.PLATFORMS;
  }, [activeFilter]);

  const isCustomOrder = String(contentOrder || "").toUpperCase() === "CUSTOM";
  // Keep the loaded pages. Custom order is applied below so the preview
  // does not flash Newest/Oldest while a new request runs.
  const fetchOrder = isCustomOrder ? "DESC" : String(contentOrder || "DESC");
  const orderedIds = normalizedPostIds;

  const postIdsKey = useMemo(() => orderedIds.join(","), [orderedIds]);
  const platformsKey = useMemo(
    () => [...platforms].sort().join(","),
    [platforms],
  );

  const hasPostIds = orderedIds.length > 0;

  const queryResult = useInfiniteQuery(
    [
      "customContentInfinite",
      user_id,
      title,
      postIdsKey,
      fetchOrder,
      platformsKey,
      shoppableStatusFilter || "all",
      CUSTOM_CONTENT_PAGE_SIZE,
    ],
    async ({ pageParam = 0 }): Promise<CustomContentPage> => {
      const start = Math.max(0, Math.floor(pageParam) || 0);
      const pageSize = CUSTOM_CONTENT_PAGE_SIZE;
      const empty: CustomContentPage = {
        data: [],
        nextOffset: start,
        totalIds: orderedIds.length,
        pageSize,
      };
      if (!hasPostIds || !user_id || !username) return empty;
      if (start >= orderedIds.length) return empty;

      const pageIds = orderedIds.slice(start, start + pageSize);
      if (!pageIds.length) return empty;

      const orderType = fetchOrder;
      const { data: result, error } = await supabase.rpc(
        "get_custom_content_orderedd",
        {
          userid: user_id,
          user_name: username,
          post_ids: pageIds,
          order_type: orderType,
          custom_order: null,
          platforms,
          limit_num: pageIds.length,
          removed_post_ids: [],
        },
      );

      if (error) {
        throw new Error(error.message);
      }

      let responseData = result?.data || result || [];
      if (!Array.isArray(responseData)) responseData = [];

      if (shoppableStatusFilter && shoppableStatusFilter !== "all") {
        const shopLinkMap = await fetchShopLinkMap(
          username,
          String(user_id),
          responseData.map((post: any) => post?.post_id),
        );
        responseData = filterPostsWithShopLinks(
          responseData,
          shoppableStatusFilter,
          shopLinkMap,
        );
      }

      return {
        data: responseData,
        nextOffset: start + pageIds.length,
        totalIds: orderedIds.length,
        pageSize,
      };
    },
    {
      enabled: Boolean(enabled && title && hasPostIds && user_id),
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      keepPreviousData: hasPostIds,
      getNextPageParam: (lastPage) => {
        if (!lastPage) return undefined;
        if (lastPage.nextOffset >= lastPage.totalIds) return undefined;
        return lastPage.nextOffset;
      },
    },
  );

  const flatData = useMemo(() => {
    const pages = queryResult.data?.pages ?? [];
    const seen = new Set<string>();
    const out: any[] = [];
    for (const page of pages) {
      for (const row of page.data || []) {
        const id = String(row?.post_id ?? "").trim();
        if (id && seen.has(id)) continue;
        if (id) seen.add(id);
        out.push(row);
      }
    }
    if (!isCustomOrder || !normalizedCustomOrder?.length) return out;
    const rank = new Map(
      normalizedCustomOrder.map((id, index) => [String(id), index]),
    );
    return [...out].sort((a, b) => {
      const left = rank.get(String(a?.post_id ?? ""));
      const right = rank.get(String(b?.post_id ?? ""));
      if (left == null && right == null) return 0;
      if (left == null) return 1;
      if (right == null) return -1;
      return left - right;
    });
  }, [queryResult.data, isCustomOrder, normalizedCustomOrder]);

  const forceRefetch = () => {
    queryClient.invalidateQueries([
      "customContentInfinite",
      user_id,
      title,
      postIdsKey,
      fetchOrder,
      platformsKey,
      shoppableStatusFilter || "all",
      CUSTOM_CONTENT_PAGE_SIZE,
    ]);
  };

  return {
    ...queryResult,
    data: { data: flatData, total_count: orderedIds.length },
    flatData,
    forceRefetch,
  };
}
